import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// in bytes, compress images larger than 1MB
const fileSizeMax = 1 * 1024 * 1024;
// in pixels, compress images have the width or height larger than 1024px
const widthHeightMax = 1024;
const defaultWidthHeightRatio = 1;
const defaultQualityRatio = 0.7;

@Injectable({
  providedIn: 'root'
})
export class CompressImageService {
  compress(file: File): Observable<File> {
    const imageType = file.type || 'image/jpeg';

    return Observable.create(observer => {
      // An object URL keeps the source out of the JS heap. Reading a 10MB camera photo
      // with readAsDataURL held a ~13MB base64 string on top of the decoded bitmap and
      // the canvas; that peak is enough for Android to kill a backgrounded renderer,
      // which the user sees as the page reloading after the file picker closes.
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      let settled = false;

      const cleanup = () => {
        URL.revokeObjectURL(objectUrl);
        img.onload = null;
        img.onerror = null;
      };

      const fail = (error) => {
        if (settled) { return; }
        settled = true;
        cleanup();
        observer.error(error);
      };

      // Bound before `src` is assigned: a cached or very small image can finish loading
      // before the next statement runs, and a handler attached afterwards would never
      // fire - which left the caller's loading spinner up forever.
      img.onload = () => {
        try {
          // Dimensions are read here rather than before load. Previously they were still
          // 0 at that point, so the ratio was always 1 and no image was ever resized.
          const imgWH = img.width > img.height ? img.width : img.height;

          // Determines the ratios to compress the image
          const withHeightRatio = (imgWH > widthHeightMax) ? widthHeightMax / imgWH : defaultWidthHeightRatio;
          const qualityRatio = (file.size > fileSizeMax) ? fileSizeMax / (1.6 * file.size) : defaultQualityRatio;

          const elem = document.createElement('canvas');
          // resize width, height
          elem.width = Math.max(1, Math.round(img.width * withHeightRatio));
          elem.height = Math.max(1, Math.round(img.height * withHeightRatio));

          const ctx = <CanvasRenderingContext2D>elem.getContext('2d');
          ctx.drawImage(img, 0, 0, elem.width, elem.height);
          ctx.canvas.toBlob(
            // callback, called when blob created
            blob => {
              if (settled) { return; }
              settled = true;
              cleanup();
              // Release the canvas backing store before handing the file back
              elem.width = 0;
              elem.height = 0;
              if (!blob) {
                observer.error(new Error('Image could not be compressed.'));
                return;
              }
              observer.next(new File(
                [blob],
                file.name,
                {
                  type: imageType,
                  lastModified: Date.now(),
                }
              ));
              observer.complete();
            },
            imageType,
            qualityRatio, // reduce image quantity
          );
        } catch (error) {
          fail(error);
        }
      };

      // Corrupt file, or a codec the browser cannot decode (e.g. HEIC straight off an iPhone)
      img.onerror = () => fail(new Error('Image could not be read.'));

      img.src = objectUrl;
    });
  }
}
